/*
 * Product-page videos that start on their own, each with a .videoToggle beside it:
 * - the PDP gallery's looping videos (snippets/product-visual-variant.liquid), in every section
 *   that renders that gallery: main-product-v1, main-product-complex-v1 and
 *   main-product-complex-v1-installments;
 * - the play-once PDP animation (sections/section-pdp-animation-v1.liquid, the MCON pages), which
 *   loads this file itself.
 *
 * The videos ship with preload="none" and no autoplay, and play only while they are on screen
 * (66ca63d took the gallery off autoplay, which downloaded every video on the page at load). A
 * gallery that is display: none, or a slide the swiper has clipped away, never intersects, so its
 * video never downloads. A looping video starts within 200px of the screen, so its first frames are
 * in when it scrolls in; a play-once animation waits until a tenth of it is on screen, or it would
 * be over before anyone saw it. An animation that has ended plays again the next time it scrolls
 * in (its copy says "Scroll to reset animation").
 *
 * Accessibility: each video runs past 5 s, so each has a pause/play toggle (WCAG 2.2.2, Pause,
 * Stop, Hide). A pause holds: scrolling or swiping back does not restart a video the shopper
 * stopped. With prefers-reduced-motion nothing starts until the shopper presses play. Only the
 * active gallery slide's toggle is visible or focusable (theme-global.css), so Tab never lands in a
 * clipped slide.
 *
 * A video with no poster (the animation) would show an empty box when it is on screen but not
 * playing: under reduced motion, or when autoplay is refused. Then only its metadata loads and it
 * seeks a hundredth of a second in, which paints the first frame. Not Shopify's preview_image as a
 * poster: for an untagged (TV-range) upload it comes out washed out (CLAUDE.md, 2026-09-30), a grey
 * box on the animation's black section.
 *
 * The toggle is the hero-video toggle's design (hero-video.js), progress ring included: it is
 * redrawn every frame while the video plays and not at all otherwise.
 */
(function () {
    if (window.ohsnapGalleryVideoRuntime) {
        return;
    }
    window.ohsnapGalleryVideoRuntime = true;

    var LABEL_PLAY = "Play video";
    var LABEL_PAUSE = "Pause video";
    var reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var states = new WeakMap();

    function syncToggle(state, playing) {
        state.toggle.toggleAttribute("data-paused", !playing);
        state.toggle.setAttribute("aria-label", playing ? LABEL_PAUSE : LABEL_PLAY);
    }

    function drawRing(state) {
        var video = state.video;
        if (state.ring && video.duration && isFinite(video.duration)) {
            var percent = Math.min(100, (video.currentTime / video.duration) * 100);
            state.ring.style.strokeDashoffset = (100 - percent).toFixed(2);
        }
    }

    function startRing(state) {
        if (state.frame) {
            return;
        }
        var tick = function () {
            drawRing(state);
            state.frame = requestAnimationFrame(tick);
        };
        state.frame = requestAnimationFrame(tick);
    }

    function stopRing(state) {
        if (state.frame) {
            cancelAnimationFrame(state.frame);
            state.frame = 0;
        }
    }

    // paints the first frame of a poster-less video that is not going to play yet
    function showFirstFrame(state) {
        var video = state.video;
        if (state.framed || video.poster || video.readyState >= 2) {
            return;
        }
        state.framed = true;
        video.addEventListener("loadedmetadata", function () {
            if (video.paused && video.currentTime === 0) {
                video.currentTime = 0.01;
            }
        }, { once: true });
        video.preload = "metadata";
        video.load();
    }

    function play(state) {
        var promise = state.video.play();
        if (promise && typeof promise.catch === "function") {
            // Low Power Mode and data saver refuse autoplay; the poster and the play toggle stay
            promise.catch(function () {
                syncToggle(state, false);
                if (state.inView) {
                    showFirstFrame(state);
                }
            });
        }
    }

    function maybePlay(state) {
        if (!state.inView || state.userPaused) {
            return;
        }
        if (reducedMotion && !state.userPlayed) {
            showFirstFrame(state);
            return;
        }
        play(state);
    }

    function onEntries(entries) {
        entries.forEach(function (entry) {
            var state = states.get(entry.target);
            if (!state) {
                return;
            }
            state.inView = state.once ? entry.intersectionRatio >= 0.1 : entry.isIntersecting;
            if (state.inView) {
                maybePlay(state);
            } else {
                state.video.pause();
            }
        });
    }

    var nearObserver = new IntersectionObserver(onEntries, { rootMargin: "200px" });
    var onScreenObserver = new IntersectionObserver(onEntries, { threshold: [0, 0.1] });

    document.addEventListener("click", function (event) {
        var toggle = event.target.closest(".videoToggle");
        var video = toggle && toggle.parentElement.querySelector("video[data-lazy-video]");
        var state = video && states.get(video);
        if (!state) {
            return;
        }

        if (video.paused) {
            // an explicit press overrides both a previous pause and reduced motion; an animation
            // that has ended starts again from the top
            state.userPaused = false;
            state.userPlayed = true;
            play(state);
        } else {
            state.userPaused = true;
            video.pause();
        }
    });

    function setUp() {
        document.querySelectorAll("video[data-lazy-video]").forEach(function (video) {
            var toggle = video.parentElement.querySelector(".videoToggle");
            if (!toggle || states.has(video)) {
                return;
            }
            var state = {
                video: video,
                toggle: toggle,
                ring: toggle.querySelector(".videoToggleRingProgress"),
                once: !video.loop,
                frame: 0,
                framed: false,
                inView: false,
                userPaused: false,
                userPlayed: false
            };
            states.set(video, state);

            video.addEventListener("play", function () {
                syncToggle(state, true);
                startRing(state);
            });
            // also fires when a play-once video reaches its end
            video.addEventListener("pause", function () {
                syncToggle(state, false);
                stopRing(state);
            });

            if (reducedMotion) {
                syncToggle(state, false);
            }
            (state.once ? onScreenObserver : nearObserver).observe(video);
        });
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", setUp);
    } else {
        setUp();
    }
    // the theme editor re-renders a section in place
    document.addEventListener("shopify:section:load", setUp);
})();
